import { CANVAS_RULER_THICKNESS } from '@blocksuite/affine-shared/styles';
import {
  streamLainMotionChannel,
  streamLainMotionScheduler,
} from '@blocksuite/affine-shared/utils';
import {
  CANVAS_BLOCK_SIZE,
  CANVAS_CELL_SIZE,
  CANVAS_SECTOR_SIZE,
  CANVAS_WORLD_MAX,
  CANVAS_WORLD_MIN,
  CANVAS_WORLD_SIZE,
} from '@blocksuite/store';

let chromeId = 0;

export interface CanvasChromeViewport {
  width: number;
  height: number;
  zoom: number;
  translateX: number;
  translateY: number;
  left: number;
  top: number;
  pixelRatio: number;
}
export interface CanvasChromePalette {
  /** Axes, ruler rules and the world title. */
  axis: string;
  /** The area outside the world. */
  outside: string;
  /** The canvas background: ruler fill and the fading grid edge. */
  surface: string;
  /** Ruler ticks and labels, the origin mark. */
  label: string;
}

export interface CanvasWorldChromeOptions {
  /** Axes, origin, rulers and world title (`CanvasContentConfig.worldMarkings`). */
  markings: boolean;
}

/** How long rulers stay after the last pan or drag before they fade out. */
export const CANVAS_RULER_IDLE_MS = 400;

const escapeXml = (value: string) =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));
const signed = (value: number) =>
  `${value < 0 ? '-' : '+'}${String(Math.abs(Math.round(value))).padStart(2, '0')}`;

export function planCanvasWorldChrome(view: CanvasChromeViewport) {
  const { width, height, zoom, translateX, translateY } = view;
  const worldLeft = translateX + CANVAS_WORLD_MIN * zoom;
  const worldTop = translateY + CANVAS_WORLD_MIN * zoom;
  const worldRight = translateX + CANVAS_WORLD_MAX * zoom;
  const worldBottom = translateY + CANVAS_WORLD_MAX * zoom;
  const materialScale = Math.min(1, zoom / 0.1);
  const thickness = CANVAS_RULER_THICKNESS * materialScale;
  const left = Math.max(0, worldLeft),
    top = Math.max(0, worldTop);
  const right = Math.min(width, worldRight),
    bottom = Math.min(height, worldBottom);
  return {
    worldLeft,
    worldTop,
    worldRight,
    worldBottom,
    left,
    top,
    right,
    bottom,
    materialScale,
    thickness,
    rulerX: clamp(
      worldLeft - thickness,
      0,
      Math.max(0, worldRight - thickness)
    ),
    rulerY: clamp(
      worldTop - thickness,
      0,
      Math.max(0, worldBottom - thickness)
    ),
    visible: right > left && bottom > top,
  };
}

/**
 * Rulers show while the view is panned or something is dragged, and fade out
 * once that has stopped for `CANVAS_RULER_IDLE_MS`.
 */
export class CanvasRulerPresence {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private shown = false;

  constructor(private readonly setVisible: (visible: boolean) => void) {}

  activity() {
    if (!this.shown) {
      this.shown = true;
      this.setVisible(true);
    }
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.shown = false;
      this.setVisible(false);
    }, CANVAS_RULER_IDLE_MS);
  }

  dispose() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }
}

/** Screen-space SVG chrome stays below document content and never intercepts input. */
export class CanvasWorldChrome {
  private readonly id = `edgeless-world-${++chromeId}`;
  private readonly graphic: SVGSVGElement;
  private readonly rulers: SVGSVGElement;
  private readonly cursor: SVGSVGElement;
  private readonly rulerChannel = streamLainMotionChannel('canvas-rulers');
  private rulerOpacity = 0;
  private view: CanvasChromeViewport | null = null;
  private palette: CanvasChromePalette | null = null;
  private pointer: { x: number; y: number } | null = null;

  constructor(
    host: HTMLElement,
    private readonly options: CanvasWorldChromeOptions
  ) {
    const create = () => {
      const svg = host.ownerDocument.createElementNS(
        'http://www.w3.org/2000/svg',
        'svg'
      );
      svg.setAttribute('aria-hidden', 'true');
      svg.style.cssText =
        'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;overflow:hidden';
      host.append(svg);
      return svg;
    };
    this.graphic = create();
    this.rulers = create();
    this.rulers.setAttribute('data-canvas-rulers', '');
    this.rulers.style.opacity = '0';
    this.cursor = create();
  }

  /** Fades the rulers in or out; reduced motion switches them at once. */
  setRulersVisible(visible: boolean) {
    streamLainMotionScheduler.animate({
      channel: this.rulerChannel,
      kind: visible ? 'canvasEnter' : 'exit',
      domain: 'canvas',
      from:
        streamLainMotionScheduler.value(this.rulerChannel) ?? this.rulerOpacity,
      to: visible ? 1 : 0,
      onUpdate: value => {
        this.rulerOpacity = value;
        this.rulers.style.opacity = String(value);
      },
    });
  }

  update(
    view: CanvasChromeViewport,
    palette: CanvasChromePalette,
    title: string,
    /** The equator lines x = 0 and y = 0 belong to the line grid only. */
    equator: boolean
  ) {
    if (
      !Object.values(view).every(Number.isFinite) ||
      view.width <= 0 ||
      view.height <= 0 ||
      view.zoom <= 0
    )
      return;
    this.view = view;
    this.palette = palette;
    const { width, height, zoom, translateX: tx, translateY: ty } = view;
    const p = planCanvasWorldChrome(view);
    const axis = escapeXml(palette.axis),
      labelColor = escapeXml(palette.label);
    const surface = escapeXml(palette.surface);
    const dpr = Math.max(0.1, view.pixelRatio);
    const stroke = Math.max(p.materialScale, 1 / dpr);
    const rect = (x: number, y: number, w: number, h: number, fill: string) =>
      `<rect x="${x}" y="${y}" width="${Math.max(0, w)}" height="${Math.max(0, h)}" fill="${fill}"/>`;
    const text = (
      x: number,
      y: number,
      content: string,
      size: number,
      rotation = 0,
      weight = 600
    ) =>
      `<text transform="translate(${x} ${y}) rotate(${rotation})" dominant-baseline="hanging" font-family="ui-monospace, monospace" font-size="${size}" font-weight="${weight}" fill="${labelColor}">${escapeXml(content)}</text>`;
    const world: string[] = [];
    const rulers: string[] = [];
    // The outside material, without masking or moving existing content.
    world.push(
      `<path fill="${escapeXml(palette.outside)}" fill-rule="evenodd" d="M0 0H${width}V${height}H0Z M${p.worldLeft} ${p.worldTop}H${p.worldRight}V${p.worldBottom}H${p.worldLeft}Z"/>`
    );
    const { markings } = this.options;
    const normalizedTitle = markings ? title.trim() : '';
    if (normalizedTitle) {
      const fontSize =
        clamp(
          CANVAS_WORLD_SIZE / Math.max(12, [...normalizedTitle].length * 0.62),
          360,
          1800
        ) * zoom;
      const y = ty + (CANVAS_WORLD_MIN - CANVAS_SECTOR_SIZE * 2) * zoom;
      if (y >= 0 && y - fontSize <= height) {
        world.push(
          `<text x="${tx}" y="${y}" text-anchor="middle" font-family="Inter,system-ui,sans-serif" font-size="${fontSize}" font-weight="700" letter-spacing="${fontSize / 60}" fill="${axis}">${escapeXml(normalizedTitle)}</text>`
        );
      }
    }
    if (p.visible) {
      // The grid fades into the canvas over the last sector of the world.
      const band = CANVAS_SECTOR_SIZE * zoom;
      // Gradient vectors run from the world border into the world.
      const fades = [
        ['l', [0, 0, 1, 0], p.worldLeft, p.top, band, p.bottom - p.top],
        ['r', [1, 0, 0, 0], p.worldRight - band, p.top, band, p.bottom - p.top],
        ['t', [0, 0, 0, 1], p.left, p.worldTop, p.right - p.left, band],
        [
          'b',
          [0, 1, 0, 0],
          p.left,
          p.worldBottom - band,
          p.right - p.left,
          band,
        ],
      ] as const;
      for (const [side, [x1, y1, x2, y2], x, y, w, h] of fades) {
        if (x + w <= 0 || x >= width || y + h <= 0 || y >= height) continue;
        const id = `${this.id}-fade-${side}`;
        world.push(
          `<defs><linearGradient id="${id}" x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"><stop offset="0" stop-color="${surface}"/><stop offset="1" stop-color="${surface}" stop-opacity="0"/></linearGradient></defs>`,
          rect(x, y, w, h, `url(#${id})`)
        );
      }
    }
    if (p.visible && markings && equator) {
      const clipId = `${this.id}-axes`;
      world.push(
        `<defs><clipPath id="${clipId}">${rect(p.left, p.top, p.right - p.left, p.bottom - p.top, 'white')}</clipPath></defs>`
      );
      // One device-aligned CSS pixel per axis.
      const axisPixels = Math.max(1, Math.round(dpr));
      const axisX =
        Math.round((view.left + tx) * dpr - axisPixels / 2) / dpr - view.left;
      const axisY =
        Math.round((view.top + ty) * dpr - axisPixels / 2) / dpr - view.top;
      world.push(
        `<g clip-path="url(#${clipId})">${rect(axisX, p.top, axisPixels / dpr, p.bottom - p.top, axis)}${rect(p.left, axisY, p.right - p.left, axisPixels / dpr, axis)}</g>`
      );
      if (tx >= p.left && tx <= p.right && ty >= p.top && ty <= p.bottom) {
        world.push(
          `<path d="M${tx - 4} ${ty}h8M${tx} ${ty - 4}v8" stroke="${labelColor}" stroke-width="${Math.max(1, 1 / dpr)}"/>`
        );
      }
    }
    if (p.visible && markings) {
      rulers.push(
        rect(p.rulerX, p.rulerY, p.right - p.rulerX, p.thickness, surface),
        rect(p.rulerX, p.rulerY, p.thickness, p.bottom - p.rulerY, surface)
      );
      const interval =
        CANVAS_CELL_SIZE * zoom >= 6
          ? CANVAS_CELL_SIZE
          : CANVAS_BLOCK_SIZE * zoom >= 6
            ? CANVAS_BLOCK_SIZE
            : CANVAS_SECTOR_SIZE;
      let ticks = '';
      const labels: string[] = [];
      const stride = Math.max(
        1,
        Math.ceil((52 * p.materialScale) / (CANVAS_SECTOR_SIZE * zoom))
      );
      for (const direction of ['x', 'y'] as const) {
        const offset = direction === 'x' ? tx : ty;
        const start = Math.max(
          CANVAS_WORLD_MIN,
          ((direction === 'x' ? p.left : p.top) - offset) / zoom
        );
        const end = Math.min(
          CANVAS_WORLD_MAX,
          ((direction === 'x' ? p.right : p.bottom) - offset) / zoom
        );
        for (
          let value = Math.ceil(start / interval) * interval;
          value <= end;
          value += interval
        ) {
          const position = offset + value * zoom;
          const length =
            value % CANVAS_SECTOR_SIZE === 0
              ? p.thickness
              : value % CANVAS_BLOCK_SIZE === 0
                ? (p.thickness * 2) / 3
                : p.thickness / 3;
          ticks +=
            direction === 'x'
              ? `M${position} ${p.rulerY + p.thickness - length}v${length}`
              : `M${p.rulerX + p.thickness - length} ${position}h${length}`;
        }
        const step = CANVAS_SECTOR_SIZE * stride;
        for (
          let value = Math.ceil(start / step) * step;
          value <= end;
          value += step
        ) {
          const position = offset + value * zoom;
          const size = (value === 0 ? 11 : 9) * p.materialScale;
          labels.push(
            direction === 'x'
              ? text(
                  position + 3 * p.materialScale,
                  p.rulerY + p.materialScale,
                  signed(value / CANVAS_SECTOR_SIZE),
                  size,
                  0,
                  value === 0 ? 700 : 600
                )
              : text(
                  p.rulerX + p.materialScale,
                  position - 3 * p.materialScale,
                  signed(-value / CANVAS_SECTOR_SIZE),
                  size,
                  -90,
                  value === 0 ? 700 : 600
                )
          );
        }
      }
      rulers.push(
        `<path d="${ticks}" stroke="${labelColor}" stroke-width="${stroke}" opacity="0.5"/>`,
        ...labels,
        rect(
          p.rulerX - stroke,
          p.rulerY - stroke,
          p.thickness + stroke * 2,
          p.thickness + stroke * 2,
          surface
        ),
        `<path d="M${p.rulerX + p.thickness} ${p.top}V${p.bottom}M${p.left} ${p.rulerY + p.thickness}H${p.right}" stroke="${axis}" stroke-width="${stroke}"/>`
      );
    }
    for (const svg of [this.graphic, this.rulers, this.cursor]) {
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    }
    this.graphic.innerHTML = world.join('');
    this.rulers.innerHTML = rulers.join('');
    this.updatePointer(this.pointer);
  }

  updatePointer(pointer: { x: number; y: number } | null) {
    this.pointer = pointer;
    if (!this.view || !this.palette) return;
    const p = planCanvasWorldChrome(this.view);
    if (
      !this.options.markings ||
      !pointer ||
      !p.visible ||
      pointer.x < p.left ||
      pointer.x > p.right ||
      pointer.y < p.top ||
      pointer.y > p.bottom
    ) {
      this.cursor.replaceChildren();
      return;
    }
    this.cursor.innerHTML = `<path d="M${pointer.x} ${p.rulerY}v${p.thickness}M${p.rulerX} ${pointer.y}h${p.thickness}" stroke="${escapeXml(this.palette.axis)}" stroke-width="${Math.max(1 / this.view.pixelRatio, 1.25 * p.materialScale)}" stroke-dasharray="${2.5 * p.materialScale}"/>`;
  }

  dispose() {
    streamLainMotionScheduler.cancel(this.rulerChannel);
    this.graphic.remove();
    this.rulers.remove();
    this.cursor.remove();
  }
}
